import CommonMultiSelectField from "components/CommonMultiSelectField";
import {
  Button,
  Fade,
  IconButton,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import {
  ATTENDANCE_OPTION,
  DEFAULT_STUDENT_ATTENDANCE_FILTER,
} from "utils/constants/student";
import { IStudentAttendanceFilter } from "utils/interfaces/student";
import { closeIcon } from "assets/images";
import OverlayBox from "components/OverlayBox";
import { isEqual } from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  CLOSE_TOOLTIP_TITLE,
  DEFAULT_PAGE_NO,
  MAX_INT_VALUE,
} from "utils/constants/constant";
import {
  getPagePayloadValues,
  hideLoaderForBatchApiCall,
  renderSelectedValues,
  selectAllHandler,
  showLoaderForBatchApiCall,
  toggleFilter,
} from "utils/helper";
import { TermListPayload, TermListSuccessPayload } from "store/term/types";
import {
  CourseListPayload,
  CourseListSuccessPayload,
} from "store/course/types";
import { OrderByOptions } from "utils/enums/sorting";
import { ITerm, ITermPagePayloadValues } from "utils/interfaces/term";
import { ICourse, ICoursePagePayloadValues } from "utils/interfaces/course";

interface IStudentAttendanceFilterProps {
  filterBox: boolean;
  setFilterBox: React.Dispatch<React.SetStateAction<boolean>>;
  handleApplyClick: (formData: IStudentAttendanceFilter) => void;
  filteredData: IStudentAttendanceFilter;
  pageNo: number;
  setPageNo: React.Dispatch<React.SetStateAction<number>>;
  setFilteredData: React.Dispatch<
    React.SetStateAction<IStudentAttendanceFilter>
  >;
  setAppliedFilter: React.Dispatch<React.SetStateAction<boolean>>;
  courseListRequest: (payload: CourseListPayload) => void;
  termListRequest: (payload: TermListPayload) => void;
  getStudentAttendanceList: (
    filterData: IStudentAttendanceFilter,
    filtered?: boolean,
    pageNum?: number
  ) => void;
}

const StudentAttendanceFilter: React.FC<IStudentAttendanceFilterProps> = ({
  filterBox,
  setFilterBox,
  handleApplyClick,
  filteredData,
  pageNo,
  setPageNo,
  setFilteredData,
  setAppliedFilter,
  getStudentAttendanceList,
  termListRequest,
  courseListRequest,
}) => {
  const [termList, setTermList] = useState<ITerm[]>([]);
  const [courseList, setCourseList] = useState<ICourse[]>([]);

  const { handleSubmit, reset, setValue, watch, control } =
    useForm<IStudentAttendanceFilter>({
      defaultValues: DEFAULT_STUDENT_ATTENDANCE_FILTER,
    });

  useEffect(() => {
    reset(filteredData ?? DEFAULT_STUDENT_ATTENDANCE_FILTER);
  }, [filteredData, setValue, filterBox, reset]);

  const onClearClick = () => {
    reset();
    setFilterBox(false);
    setAppliedFilter(false);
    document.body.classList.toggle("filter-open");
    if (pageNo === DEFAULT_PAGE_NO) {
      getStudentAttendanceList(DEFAULT_STUDENT_ATTENDANCE_FILTER);
    } else {
      setPageNo(DEFAULT_PAGE_NO);
    }
    setFilteredData(DEFAULT_STUDENT_ATTENDANCE_FILTER);
  };

  const onTermListSuccess = (response: TermListSuccessPayload) => {
    setTermList(response.termList);
    hideLoaderForBatchApiCall();
  };

  const getTermList = () => {
    showLoaderForBatchApiCall();
    const values: ITermPagePayloadValues = getPagePayloadValues(
      "TermName",
      OrderByOptions.Asc,
      DEFAULT_PAGE_NO,
      MAX_INT_VALUE
    );
    values.termName = "";

    const payload = {
      values,
      callback: onTermListSuccess,
    };

    termListRequest(payload);
  };

  const onCourseListSuccess = (response: CourseListSuccessPayload) => {
    setCourseList(response.courseList);
    hideLoaderForBatchApiCall();
  };

  const getCourseList = () => {
    showLoaderForBatchApiCall();
    const values: ICoursePagePayloadValues = getPagePayloadValues(
      "Name",
      OrderByOptions.Asc,
      DEFAULT_PAGE_NO,
      MAX_INT_VALUE
    );
    values.name = "";
    const payload = {
      values,
      callback: onCourseListSuccess,
    };

    courseListRequest(payload);
  };

  useEffect(() => {
    if (filterBox) {
      getTermList();
      getCourseList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterBox]);

  const termOptions = termList.map((term) => ({
    value: term.id.toString(),
    label: term.termName,
  }));

  const yearsOptions = Array.from(
    new Set(termList.map((record) => record.year))
  )
    .sort((a, b) => a - b)
    .map((year) => ({
      value: year.toString(),
      label: year.toString(),
    }));

  const courseOptions = courseList.map((course) => ({
    value: course.id.toString(),
    label: course.name,
  }));

  const handleFieldChange = (
    event: SelectChangeEvent<string[]>,
    field: keyof IStudentAttendanceFilter
  ) => {
    let { value } = event.target;

    if (field === "years") {
      value = selectAllHandler(value as string[], yearsOptions, watch("years"));
    } else if (field === "studentAttendance") {
      value = selectAllHandler(
        value as string[],
        ATTENDANCE_OPTION,
        watch("studentAttendance")
      );
    } else if (field === "termIds") {
      value = selectAllHandler(
        value as string[],
        termOptions,
        watch("termIds")
      );
    } else if (field === "courseIds") {
      value = selectAllHandler(
        value as string[],
        courseOptions,
        watch("courseIds")
      );
    }
    setValue(field, value as string[]);
  };

  return (
    <>
      <form onSubmit={handleSubmit(handleApplyClick)}>
        {filterBox && <OverlayBox setFilterBox={setFilterBox} />}
        <Box className="filter-popover">
          <Box className="filter-header">
            <Typography variant="h4">Filter</Typography>
            <Tooltip
              TransitionComponent={Fade}
              TransitionProps={{ timeout: 600 }}
              title={CLOSE_TOOLTIP_TITLE}
              arrow
            >
              <IconButton
                onClick={() => {
                  setFilterBox(false);
                  toggleFilter();
                }}
              >
                <img src={closeIcon} alt="close" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box className="filter-body">
            <CommonMultiSelectField<IStudentAttendanceFilter>
              name="years"
              label="Select Year"
              control={control}
              options={yearsOptions}
              isSelectAllSelected={
                watch("years")?.length === yearsOptions.length
              }
              onChange={(field) => {
                handleFieldChange(field, "years");
              }}
              renderSelectedValues={renderSelectedValues}
            />
            <CommonMultiSelectField<IStudentAttendanceFilter>
              name="studentAttendance"
              label="Select Attendance"
              control={control}
              options={ATTENDANCE_OPTION}
              onChange={(field) => {
                handleFieldChange(field, "studentAttendance");
              }}
              renderSelectedValues={renderSelectedValues}
              displaySelectAll={false}
            />
            <CommonMultiSelectField<IStudentAttendanceFilter>
              name="termIds"
              label="Select Terms"
              control={control}
              options={termOptions}
              onChange={(field) => {
                handleFieldChange(field, "termIds");
              }}
              isSelectAllSelected={
                watch("termIds")?.length === termOptions.length
              }
              renderSelectedValues={renderSelectedValues}
            />
            <CommonMultiSelectField<IStudentAttendanceFilter>
              name="courseIds"
              label="Select Courses"
              control={control}
              options={courseOptions}
              onChange={(field) => {
                handleFieldChange(field, "courseIds");
              }}
              isSelectAllSelected={
                watch("courseIds")?.length === courseOptions.length
              }
              renderSelectedValues={renderSelectedValues}
            />
          </Box>
          <Box className="filter-footer">
            <Button variant="contained" className="btn-apply" type="submit">
              Apply
            </Button>
            <Button
              variant="outlined"
              className="btn-clear"
              onClick={onClearClick}
              disabled={isEqual(DEFAULT_STUDENT_ATTENDANCE_FILTER, watch())}
            >
              Clear
            </Button>
          </Box>
        </Box>
      </form>
    </>
  );
};

export default StudentAttendanceFilter;
